/**
 * Client-side execution engine: maps Claude `executeAction` to idwiz Okta API routes.
 */
import { retrieveToken } from "./tokenVault.js";
import { isAuth0Project } from "../utils/projectProvider.js";

/**
 * @param {string} endpoint
 * @param {Record<string, unknown>} body
 */
async function post(endpoint, body) {
  const res = await fetch(endpoint, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  /** @type {Record<string, unknown>} */
  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  return { ...data, _status: res.status };
}

/**
 * Resolve a policy from the live list by name: exact match first, then substring (both directions).
 * @param {Array<{ id?: string, name?: string, isStrong?: boolean }>} policies
 * @param {string} name
 */
function resolvePolicyByName(policies, name) {
  const n = String(name || "").trim();
  if (!n) return null;
  const lower = n.toLowerCase();
  return (
    policies.find((p) => String(p.name || "").toLowerCase() === lower) ||
    policies.find(
      (lp) =>
        String(lp.name || "").toLowerCase().includes(lower) ||
        lower.includes(String(lp.name || "").toLowerCase()),
    ) ||
    null
  );
}

/**
 * @param {unknown} action
 * @param {{ org?: { url?: string }, projectId?: string } | null} project
 */
export async function executeAction(action, project) {
  if (!action || typeof action !== "object" || !action.type) return null;

  if (action.type === "comprehensive_assessment" && isAuth0Project(project)) {
    return await post("/api/idp/auth0/org-audit", {
      projectId: project?.projectId,
      trigger: "manual",
    });
  }

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log("[executionEngine] called with action:", action?.type, action?.params);
  }

  const orgUrl = project?.org?.url ? String(project.org.url).trim() : "";
  const apiToken = orgUrl ? retrieveToken(orgUrl) : null;

  if (!apiToken) {
    return {
      ok: false,
      error: "No API token available",
      detail: "Token expired or not set. Please reconnect.",
      _status: 401,
    };
  }

  const base = {
    oktaUrl: orgUrl,
    orgUrl,
    apiToken,
    projectId: project?.projectId,
  };

  try {
    switch (action.type) {
      case "create_saml_app":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "create_saml_app",
          params: {
            appName: action.params?.appName,
            ssoUrl: action.params?.ssoUrl,
            audience: action.params?.audience,
            attributes: action.params?.attributes,
            nameIdFormat: action.params?.nameIdFormat || "email",
          },
        });

      case "create_oidc_app":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "create_oidc_app",
          params: {
            appName: action.params?.appName,
            appUrl: action.params?.appUrl,
            applicationType: action.params?.applicationType || "web",
            grantTypes: action.params?.grantTypes || ["authorization_code", "refresh_token"],
            redirectUris: action.params?.redirectUris || [],
            postLogoutUris: action.params?.postLogoutUris || action.params?.post_logout_uris || [],
            pkce: action.params?.pkce ?? true,
          },
        });

      case "create_swa_app":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "create_swa_app",
          params: {
            appName: action.params?.appName,
            appUrl: action.params?.appUrl,
            loginUrl: action.params?.loginUrl,
          },
        });

      case "create_user":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "create_user",
          params: {
            firstName: action.params?.firstName,
            lastName: action.params?.lastName,
            email: action.params?.email,
            login: action.params?.login || action.params?.email,
          },
        });

      case "add_oin_app": {
        const appName = action.params?.appName || action.params?.templateName;
        if (!appName || typeof appName !== "string") {
          return {
            ok: false,
            error: "add_oin_app requires params.appName (OIN catalog key)",
            _status: 400,
          };
        }
        const schemaRes = await post("/api/idp/okta/oin-schema", {
          ...base,
          appName,
        });
        if (schemaRes.ok === false || (schemaRes._status != null && schemaRes._status >= 400)) {
          return schemaRes;
        }
        const modes = Array.isArray(schemaRes.signOnModes) ? schemaRes.signOnModes : [];
        const requested = action.params?.signOnMode;
        let signOnMode = "SAML_2_0";
        if (requested && modes.includes(requested)) {
          signOnMode = requested;
        } else if (modes.includes("SAML_2_0")) {
          signOnMode = "SAML_2_0";
        } else if (modes.length > 0) {
          signOnMode = modes[0];
        }
        return await post("/api/operations/propose", {
          ...base,
          operationId: "add_oin_app",
          params: {
            appName,
            label: action.params?.label || action.params?.appName || appName,
            signOnMode,
            settings: {
              general: action.params?.generalSettings || action.params?.app || {},
              sso: action.params?.ssoSettings || action.params?.signOn || {},
            },
          },
        });
      }

      case "enable_threatinsight":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "enable_threatinsight",
          params: {
            mode: action.params?.mode || "ENFORCE",
          },
        });

      case "enable_deprovisioning":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "enable_deprovisioning",
          params: {
            appIds: action.params?.appIds || [],
          },
        });

      case "list_password_policies":
      case "list_password_policy":
      case "get_password_policies":
        return await post("/api/idp/okta/policies/password/list", { ...base });

      case "update_password_policy": {
        const listData = await post("/api/idp/okta/policies/password/list", { ...base });
        if (listData.ok === false || (listData._status != null && listData._status >= 400)) {
          return {
            ok: false,
            error: listData.errorSummary || listData.error || "Could not list policies",
            _status: listData._status,
          };
        }
        const policies = Array.isArray(listData.policies) ? listData.policies : [];
        /** @type {typeof policies} */
        let toUpdate = [];

        const rawPid = action.params?.policyId != null && String(action.params.policyId).trim()
          ? String(action.params.policyId).trim()
          : null;

        if (rawPid) {
          const found = policies.find((p) => String(p.id) === String(rawPid));
          if (!found) {
            return { ok: false, error: `No policy with id ${rawPid} in live list`, _status: 404 };
          }
          if (found.isStrong) {
            return {
              ok: true,
              message: `${found.name} is already compliant — no update needed`,
              updated: 0,
              failed: 0,
              total: 0,
              results: [],
            };
          }
          toUpdate = [found];
        } else if (Array.isArray(action.params?.policies)) {
          for (const p of action.params.policies) {
            const name = String(p.policyName || p.name || "").trim();
            if (!name) continue;
            const match = resolvePolicyByName(policies, name);
            if (match && match.isStrong) {
              // eslint-disable-next-line no-console
              console.warn("[executionEngine] Skipping", match.name, "— already strong, id:", match.id);
              continue;
            }
            if (match && match.name === "LDAP Policy") {
              // eslint-disable-next-line no-console
              console.warn("[executionEngine] Skipping LDAP Policy — system policy, already compliant");
              continue;
            }
            if (match && !match.isStrong) {
              if (!toUpdate.some((x) => String(x.id) === String(match.id))) toUpdate.push(match);
            } else if (!match) {
              // eslint-disable-next-line no-console
              console.warn("[executionEngine] Could not find policy:", name);
            }
          }
        } else if (action.params?.policyName != null && String(action.params.policyName).trim()) {
          const name = String(action.params.policyName);
          const match = resolvePolicyByName(policies, name);
          if (match && match.isStrong) {
            return {
              ok: true,
              message: `${match.name} is already compliant — no update needed`,
              updated: 0,
              failed: 0,
              total: 0,
              results: [],
            };
          }
          if (match && !match.isStrong) {
            toUpdate = [match];
          }
        } else {
          toUpdate = policies.filter((p) => !p.isStrong);
        }

        if (!toUpdate.length) {
          return {
            ok: true,
            message: "All targeted policies are already compliant",
            updated: 0,
            failed: 0,
            total: 0,
            results: [],
          };
        }

        // eslint-disable-next-line no-console
        console.log("[executionEngine] Updating policies:", toUpdate.map((p) => `${p.name} (${p.id})`));

        if (toUpdate.length === 1) {
          return await post("/api/operations/propose", {
            ...base,
            operationId: "update_password_policy",
            params: { policyId: String(toUpdate[0].id) },
          });
        }

        const results = [];
        for (const policy of toUpdate) {
          // eslint-disable-next-line no-console
          console.log("[executionEngine] Updating:", policy.name, policy.id);
          // Server applies hardcoded BP-AUTH-03 (Classic min* complexity); do not send client settings (use* fields confuse HAR/debug).
          const updateData = await post("/api/idp/okta/policies/password/update", {
            ...base,
            policyId: policy.id,
          });
          // eslint-disable-next-line no-console
          console.log(
            "[executionEngine] Result for",
            policy.name,
            ":",
            updateData.ok,
            updateData.errorSummary || updateData.error || "",
            updateData.detail != null
              ? `\n  oktaDetail: ${
                  typeof updateData.detail === "string"
                    ? updateData.detail
                    : JSON.stringify(updateData.detail)
                }`
              : "",
          );
          const okRow =
            updateData.ok === true ||
            (updateData._status != null && updateData._status >= 200 && updateData._status < 300);
          results.push({
            name: policy.name,
            id: policy.id,
            ok: okRow,
            newMinLength:
              updateData.newMinLength ?? updateData.policy?.settings?.password?.complexity?.minLength,
            error: updateData.errorSummary || updateData.error,
          });
        }

        const succeeded = results.filter((r) => r.ok).length;
        return {
          ok: succeeded > 0,
          updated: succeeded,
          failed: results.length - succeeded,
          total: results.length,
          results,
        };
      }

      case "fix_all_weak_password_policies":
      case "update_all_weak_password_policies":
      case "update_all_password_policies":
        return {
          ok: false,
          error:
            "Batch operations are temporarily disabled; iterate single-policy updates instead.",
          _status: 400,
        };

      case "list_auth_policies":
      case "list_authentication_policies":
        return await post("/api/idp/okta/policies/auth/list", { ...base });

      case "list_signin_policies":
      case "list_legacy_policies":
        return await post("/api/idp/okta/policies/signin/list", { ...base });

      case "list_authenticators":
      case "list_mfa_factors":
      case "list_factors":
        return await post("/api/idp/okta/authenticators/list", { ...base });

      case "activate_authenticator":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "activate_authenticator",
          params: {
            authenticatorId: action.params?.authenticatorId,
          },
        });

      case "deactivate_authenticator":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "deactivate_authenticator",
          params: {
            authenticatorId: action.params?.authenticatorId,
          },
        });

      case "list_mfa_policies":
        return await post("/api/idp/okta/policies/mfa/list", { ...base });

      case "update_mfa_policy":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "update_mfa_policy",
          params: {
            policyId: action.params?.policyId,
            settings: action.params?.settings ?? {},
          },
        });

      case "scan_policies":
      case "audit_policies": {
        const settled = await Promise.allSettled([
          post("/api/idp/okta/policies/password/list", { ...base }),
          post("/api/idp/okta/policies/auth/list", { ...base }),
          post("/api/idp/okta/policies/signin/list", { ...base }),
        ]);
        const pwd = settled[0].status === "fulfilled" ? settled[0].value : {};
        const auth = settled[1].status === "fulfilled" ? settled[1].value : {};
        const signin = settled[2].status === "fulfilled" ? settled[2].value : {};
        const legacyPolicies = Array.isArray(signin.policies) ? signin.policies : [];
        const totalLegacyActive = legacyPolicies.filter((p) => p.status === "ACTIVE").length;
        return {
          ok: true,
          passwordPolicies: pwd.policies || [],
          authPolicies: auth.policies || [],
          legacyPolicies,
          totalLegacyActive,
          anyLegacyActive: signin.anyActive === true,
        };
      }

      case "comprehensive_assessment":
        return await post("/api/idp/okta/org-audit", { ...base });

      case "list_groups":
        return await post("/api/idp/okta/groups/list", {
          ...base,
          q: action.params?.q ?? null,
          search: action.params?.search ?? null,
          limit: action.params?.limit ?? 20,
          type: action.params?.type ?? null,
        });

      case "search_groups":
        return await post("/api/idp/okta/groups/list", {
          ...base,
          q: action.params?.q || action.params?.name || null,
          limit: action.params?.limit ?? 20,
        });

      case "create_group":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "create_group",
          params: {
            name: action.params?.name,
            description: action.params?.description || "",
          },
        });

      case "create_and_assign_group":
        return await post("/api/operations/propose", {
          ...base,
          operationId: "create_and_assign_group",
          params: {
            appId: action.params?.appId,
            groupName: action.params?.groupName,
            groupDescription: action.params?.groupDescription || "",
          },
        });

      case "assign_app_to_group":
        if (!action.params?.appId || !action.params?.groupId) {
          return {
            ok: false,
            error:
              "appId and groupId required. Use list_groups first to search by name and get the groupId.",
            _status: 400,
          };
        }
        return await post("/api/operations/propose", {
          ...base,
          operationId: "assign_app_to_group",
          params: {
            appId: action.params?.appId,
            groupId: action.params?.groupId,
            priority: action.params?.priority ?? 0,
          },
        });

      case "get_app_groups":
        return await post("/api/idp/okta/apps/groups/list", {
          ...base,
          appId: action.params?.appId,
          limit: action.params?.limit || 100,
        });

      case "get_app_metadata":
        if (!action.params?.appId) {
          return {
            ok: false,
            error: "get_app_metadata requires params.appId",
            _status: 400,
          };
        }
        return await post("/api/idp/okta/apps/get", {
          ...base,
          appId: action.params?.appId,
        });

      case "list_apps":
      case "scan_apps":
        return await post("/api/idp/okta/apps/list", {
          ...base,
          limit: action.params?.limit ?? 50,
          q: action.params?.q ?? null,
          filter: action.params?.filter ?? null,
        });

      case "query_system_log":
      case "search_logs":
      case "get_logs":
        return await post("/api/idp/okta/logs/query", {
          ...base,
          filter: action.params?.filter ?? null,
          since: action.params?.since ?? null,
          until: action.params?.until ?? null,
          sortOrder: action.params?.sortOrder ?? "DESCENDING",
          limit: action.params?.limit ?? 25,
          q: action.params?.q ?? null,
        });

      default:
        return {
          ok: false,
          error: `Unknown action type: ${action.type}`,
          _status: 400,
        };
    }
  } catch (err) {
    return {
      ok: false,
      error: /** @type {Error} */ (err)?.message || String(err),
      detail: "Network or server error",
      _status: 0,
    };
  }
}
